import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, Star, Plus, Minus, ArrowLeft, Truck, Shield } from 'lucide-react';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import './ProductPage.css';

export default function ProductPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [review, setReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`)
      .then(res => { setProduct(res.data.product); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    try {
      const res = await api.post(`/products/${id}/reviews`, review);
      setProduct(res.data.product);
      setReview({ rating: 5, comment: '' });
      toast.success('Review submitted!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}><div className="spinner" /></div>;
  if (!product) return <div className="no-product container"><h2>Product not found</h2><Link to="/shop" className="btn-primary">Back to Shop</Link></div>;

  const images = product.images?.length > 0 ? product.images : [product.thumbnail || 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=800'];

  return (
    <div className="product-page container">
      <Link to="/shop" className="back-link"><ArrowLeft size={16} /> Back to Shop</Link>

      <div className="product-detail">
        {/* Images */}
        <div className="product-images">
          <img src={images[activeImg]} alt={product.name} className="main-image" />
          {images.length > 1 && (
            <div className="image-thumbs">
              {images.map((img, i) => (
                <img key={i} src={img} alt="" className={`thumb ${i === activeImg ? 'active' : ''}`} onClick={() => setActiveImg(i)} />
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="product-info">
          <div className="product-category-tag">{product.category}</div>
          <h1>{product.name}</h1>
          {product.origin && <p className="product-origin">🌍 {product.origin}</p>}

          {product.rating > 0 && (
            <div className="product-rating-display">
              <div className="stars">{'★'.repeat(Math.round(product.rating))}{'☆'.repeat(5 - Math.round(product.rating))}</div>
              <span>{product.rating.toFixed(1)} ({product.numReviews} reviews)</span>
            </div>
          )}

          <div className="product-price-display">CHF {product.price.toFixed(2)}</div>

          {product.flavor?.length > 0 && (
            <div className="flavor-tags">
              <strong>Flavor Notes:</strong>
              <div className="tags">{product.flavor.map(f => <span key={f} className="flavor-tag">{f}</span>)}</div>
            </div>
          )}

          {product.roastLevel && (
            <div className="roast-info"><strong>Roast:</strong> <span className="roast-badge">{product.roastLevel}</span></div>
          )}

          <p className="product-description">{product.description}</p>

          <div className="quantity-row">
            <div className="qty-control">
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))}><Minus size={16} /></button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(q => Math.min(product.stock, q + 1))} disabled={quantity >= product.stock}><Plus size={16} /></button>
            </div>
            <button className="btn-gold add-to-cart-btn" onClick={() => addItem(product, quantity)} disabled={product.stock === 0}>
              <ShoppingCart size={18} />
              {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
            </button>
          </div>

          {product.stock > 0 && product.stock <= 10 && (
            <p className="low-stock">⚠️ Only {product.stock} left in stock!</p>
          )}

          <div className="product-perks">
            <div><Truck size={16} /> Free shipping over CHF 50</div>
            <div><Shield size={16} /> 100% satisfaction guarantee</div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <div className="reviews-section">
        <h2>Customer Reviews</h2>
        {product.reviews?.length === 0 && <p className="no-reviews">No reviews yet. Be the first!</p>}
        <div className="reviews-list">
          {product.reviews?.map(r => (
            <div key={r._id} className="review">
              <div className="review-header">
                <strong>{r.name}</strong>
                <div className="stars">{Array(r.rating).fill('★').join('')}{Array(5 - r.rating).fill('☆').join('')}</div>
                <span>{new Date(r.createdAt).toLocaleDateString()}</span>
              </div>
              <p>{r.comment}</p>
            </div>
          ))}
        </div>

        {user && (
          <form className="review-form" onSubmit={submitReview}>
            <h3>Write a Review</h3>
            <div className="form-group">
              <label>Rating</label>
              <div className="star-select">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" className={`star-btn ${n <= review.rating ? 'active' : ''}`}
                    onClick={() => setReview(r => ({ ...r, rating: n }))}>★</button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Comment</label>
              <textarea className="form-control" rows={4} placeholder="Share your experience..."
                value={review.comment} onChange={e => setReview(r => ({ ...r, comment: e.target.value }))} required />
            </div>
            <button type="submit" className="btn-primary" disabled={submittingReview}>
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
